import { useState } from "react";
import { useApi } from "../lib/useApi";
import type { AuditEntry } from "../lib/types";
import { formatDate } from "../lib/format";
import { Badge, Button, EmptyState, ErrorState, Input, Modal, PageHeader, Select, Spinner, Table, Td } from "../components/ui";

const PAGE_SIZE = 50;

const PLATFORM_TONE = { free_tools: "green", paid: "purple", auth: "blue", admin: "slate" } as const;

export default function AuditLog() {
  const [filters, setFilters] = useState({ action: "", admin: "", target_type: "", search: "" });
  const [applied, setApplied] = useState(filters);
  const [offset, setOffset] = useState(0);
  const [detail, setDetail] = useState<AuditEntry | null>(null);

  const params = new URLSearchParams();
  if (applied.action) params.set("action", applied.action);
  if (applied.admin) params.set("admin", applied.admin);
  if (applied.target_type) params.set("target_type", applied.target_type);
  if (applied.search) params.set("search", applied.search);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(offset));

  const { data, loading, error, reload } = useApi<{ entries: AuditEntry[]; total: number }>(
    `/audit-log?${params.toString()}`,
  );
  const filterOptions = useApi<{ actions: string[]; admins: string[]; targetTypes: string[] }>("/audit-log/filters");

  return (
    <div>
      <PageHeader title="Audit log" subtitle="Every admin action across all platforms, immutably recorded" />

      <form
        className="mb-4 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setOffset(0);
          setApplied(filters);
        }}
      >
        <Select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
          <option value="">All actions</option>
          {(filterOptions.data?.actions ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
        <Select value={filters.admin} onChange={(e) => setFilters({ ...filters, admin: e.target.value })}>
          <option value="">All admins</option>
          {(filterOptions.data?.admins ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
        <Select value={filters.target_type} onChange={(e) => setFilters({ ...filters, target_type: e.target.value })}>
          <option value="">All target types</option>
          {(filterOptions.data?.targetTypes ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search target / details…"
          className="max-w-xs"
        />
        <Button type="submit" variant="secondary">
          Apply
        </Button>
      </form>

      {loading ? (
        <Spinner />
      ) : error || !data ? (
        <ErrorState message={error ?? "Failed to load"} onRetry={reload} />
      ) : data.entries.length === 0 ? (
        <EmptyState message="No audit entries match." />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <Table headers={["When", "Admin", "Action", "Target", "Platform", "IP", ""]}>
            {data.entries.map((entry) => (
              <tr key={entry.id}>
                <Td className="whitespace-nowrap text-xs">{formatDate(entry.created_at)}</Td>
                <Td className="text-xs">{entry.admin_email}</Td>
                <Td className="font-mono text-xs">{entry.action}</Td>
                <Td className="text-xs">
                  {entry.target_id ?? "—"}
                  {entry.target_type && <span className="block text-slate-400">{entry.target_type}</span>}
                </Td>
                <Td>
                  {entry.target_platform ? (
                    <Badge tone={PLATFORM_TONE[entry.target_platform as keyof typeof PLATFORM_TONE] ?? "slate"}>
                      {entry.target_platform.replace("_", " ")}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td className="font-mono text-xs">{entry.ip_address ?? "—"}</Td>
                <Td>
                  {entry.details && (
                    <button className="text-xs text-rail-600 hover:underline" onClick={() => setDetail(entry)}>
                      Details
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Previous
              </Button>
              <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= data.total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <Modal title={`${detail.action} — ${formatDate(detail.created_at)}`} onClose={() => setDetail(null)}>
          <pre className="max-h-96 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
            {(() => {
              try {
                return JSON.stringify(JSON.parse(detail.details ?? "null"), null, 2);
              } catch {
                return detail.details;
              }
            })()}
          </pre>
        </Modal>
      )}
    </div>
  );
}
